import React from "react";
import { Button } from "@/ui/components/ui/button.tsx";
import { Input } from "@/ui/components/ui/input.tsx";
import { Label } from "@/ui/components/ui/label.tsx";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/ui/components/ui/sheet.tsx";
import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";

interface UploadForm {
  name: string;
  checksum: string;
  epsg_code: string;
  file: File | null;
}

interface UploadPolygonModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  uploadForm: UploadForm;
  isAnalyzing: boolean;
  isUploading: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFormChange: (updates: Partial<UploadForm>) => void;
  onUpload: () => void;
  onCancel: () => void;
}

export const UploadPolygonModal: React.FC<UploadPolygonModalProps> = ({
  isOpen,
  onOpenChange,
  uploadForm,
  isAnalyzing,
  isUploading,
  onFileChange,
  onFormChange,
  onUpload,
  onCancel,
}) => {
  const { t } = useTranslation("polygon");

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[500px]">
        <SheetHeader>
          <SheetTitle>{t("upload.title")}</SheetTitle>
        </SheetHeader>
        
        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label htmlFor="file">{t("upload.fileLabel")}</Label>
            <Input
              id="file"
              type="file"
              accept=".dxf"
              onChange={onFileChange}
              disabled={isAnalyzing}
            />
            {isAnalyzing && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                {t("upload.analyzing")}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t("upload.nameLabel")}</Label>
            <Input
              id="name"
              value={uploadForm.name}
              onChange={(e) => onFormChange({ name: e.target.value })}
              placeholder={t("upload.namePlaceholder")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="checksum">{t("upload.checksumLabel")}</Label>
            <Input
              id="checksum"
              value={uploadForm.checksum}
              onChange={(e) => onFormChange({ checksum: e.target.value })}
              placeholder={t("upload.checksumPlaceholder")}
              readOnly
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="epsg_code">{t("upload.epsgLabel")}</Label>
            <Input
              id="epsg_code"
              value={uploadForm.epsg_code}
              onChange={(e) => onFormChange({ epsg_code: e.target.value })}
              placeholder={t("upload.epsgPlaceholder")}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={onUpload}
              disabled={isUploading || isAnalyzing}
              className="flex-1"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  {t("upload.submitting")}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t("upload.submit")}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isUploading}
            >
              {t("upload.cancel")}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
